import {CreateWorkdayCommand, mapiqApi} from '@lib/infrastructure';
import {systemDateFormat, withPayloadType} from '@lib/utils';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {format, parseISO} from 'date-fns';
import {AppThunkApiConfig, ExtraArgs} from '../../createStore';
import {Workday} from '../entities';

export const loadWorkdays = createAsyncThunk<Workday[], {from: string; to: string}, AppThunkApiConfig>(
  'workday/loadWorkdays',
  async ({from, to}, {extra}) => {
    return loadWorkdaysForDateRange(extra, from, to);
  },
);

/**
 * hardRefreshWorkdays replaces the complete WorkDay state. In constrast to loadWorkdays which partially updates the state and keeps old entries.
 * This function should not be used for small date ranges when the calendar view must be maintained.
 */
export const hardRefreshWorkdays = createAsyncThunk<Workday[], {from: string; to: string}, AppThunkApiConfig>(
  'workday/hardRefreshWorkdays',
  async ({from, to}, {extra}) => {
    return loadWorkdaysForDateRange(extra, from, to);
  },
);

export const createWorkday = createAsyncThunk<Workday, CreateWorkdayCommand, AppThunkApiConfig>(
  'workday/createWorkday',
  async (data, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const body = {
      nodeId: data.nodeId,
      startDate: format(parseISO(data.startDate), systemDateFormat),
      endDate: format(parseISO(data.startDate), systemDateFormat),
      status: data.status,
    };
    const response = await api.postWorkday(body);
    return response;
  },
);

export const updateWorkday = createAsyncThunk<Workday, Workday, AppThunkApiConfig>(
  'workday/updateWorkday',
  async (workday, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const data = {
      id: workday.id,
      nodeId: workday.nodeId,
      status: workday.status,
    } as Workday;

    const response = await api.putWorkday(data);
    return response;
  },
);

export const cancelWorkday = createAction('workday/workdayCanceled', withPayloadType<string>());

export const replaceWorkday = createAction('workday/workdayReplaced', withPayloadType<Workday>());

async function loadWorkdaysForDateRange(extra: ExtraArgs, from: string, to: string) {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  const startDate = format(parseISO(from), systemDateFormat);
  const endDate = format(parseISO(to), systemDateFormat);
  const response = await api.getWorkdays(startDate, endDate);
  return response;
}
